/**
 * Member Cohorts — the Defensora onboarding funnel + group.
 *
 * A cohort is a hand-picked set of members with a selection funnel
 * (candidate → selected → invited → accepted → enrolled). Staff nominate
 * candidates (by hand or from a dynamic, auto-syncing source), select them,
 * invite the cohort to a training program (templated), track who accepts, and
 * the accepted set is what scheduling sends session invites to.
 * API: /api/workforce/cohorts.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Users,
  Plus,
  X,
  UserPlus,
  Search,
  Send,
  GraduationCap,
  ChevronRight,
  Loader2,
  Layers,
  RefreshCw,
  CheckCircle2,
  Trash2,
  Link2,
} from 'lucide-react';
import { getWeaveinApiUrl } from '@/src/config';
import { PageHeader, PageContainer } from '@/components/weavein';
import { authFetch } from '@/lib/authFetch';

const api = (p: string) => getWeaveinApiUrl(p);

interface CohortSummary {
  id: number;
  name: string;
  description: string | null;
  status: string;
  lms_course_title: string | null;
  total: number;
  candidates: number;
  selected: number;
  invited: number;
  accepted: number;
  enrolled: number;
}
interface CohortMember {
  id: number;
  member_id: number;
  status: string;
  source: string;
  name: string;
  email: string | null;
  phone: string | null;
}
interface SeriesSummary {
  series_id: string | number;
  series_title: string;
  sessions: number;
}
interface LinkedSource {
  id: number;
  source_type: string;
  source_ref: string | null;
  source_label: string | null;
  default_status: string;
  auto_sync: boolean;
  last_synced_at: string | null;
  last_added: number | null;
}

const STATUS_ORDER = ['candidate', 'selected', 'invited', 'accepted', 'enrolled', 'completed', 'declined', 'withdrawn'];
const STATUS_COLORS: Record<string, string> = {
  candidate: 'bg-blue-100 text-blue-700',
  selected: 'bg-amber-100 text-amber-700',
  invited: 'bg-indigo-100 text-indigo-700',
  accepted: 'bg-emerald-100 text-emerald-700',
  enrolled: 'bg-teal-100 text-teal-700',
  completed: 'bg-teal-100 text-teal-700',
  declined: 'bg-red-100 text-red-700',
  withdrawn: 'bg-gray-100 text-gray-600',
};
// The forward funnel step from each status
const NEXT: Record<string, string> = {
  candidate: 'selected',
  selected: 'invited',
  invited: 'accepted',
  accepted: 'enrolled',
  enrolled: 'completed',
};

type DetailTab = 'members' | 'sources' | 'acceptance';

export default function CohortsPage() {
  const [cohorts, setCohorts] = useState<CohortSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<CohortSummary | null>(null);
  const [members, setMembers] = useState<CohortMember[]>([]);
  const [tab, setTab] = useState<DetailTab>('members');
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [showMessage, setShowMessage] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const loadCohorts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(api('/api/workforce/cohorts'));
      if (res.ok) setCohorts((await res.json()).cohorts || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCohorts();
  }, [loadCohorts]);

  const openCohort = async (c: CohortSummary) => {
    setSelected(c);
    setTab('members');
    setMembers([]);
    const res = await authFetch(api(`/api/workforce/cohorts/${c.id}`));
    if (res.ok) setMembers((await res.json()).members || []);
  };

  const refreshDetail = async () => {
    if (!selected) return;
    const res = await authFetch(api(`/api/workforce/cohorts/${selected.id}`));
    if (res.ok) setMembers((await res.json()).members || []);
    loadCohorts();
  };

  const createCohort = async () => {
    if (!newName.trim()) return;
    const res = await authFetch(api('/api/workforce/cohorts'), { method: 'POST', jsonBody: { name: newName.trim() } });
    if (res.ok) {
      setNewName('');
      setShowCreate(false);
      await loadCohorts();
    }
  };

  const setMemberStatus = async (memberId: number, status: string) => {
    if (!selected) return;
    await authFetch(api(`/api/workforce/cohorts/${selected.id}/members/${memberId}`), {
      method: 'PATCH',
      jsonBody: { status },
    });
    await refreshDetail();
  };

  const bulkSetStatus = async (fromStatus: string, toStatus: string) => {
    if (!selected) return;
    const ids = members.filter((m) => m.status === fromStatus).map((m) => m.member_id);
    await Promise.all(
      ids.map((id) =>
        authFetch(api(`/api/workforce/cohorts/${selected.id}/members/${id}`), {
          method: 'PATCH',
          jsonBody: { status: toStatus },
        }),
      ),
    );
    await refreshDetail();
  };

  const acceptedCount = members.filter((m) => m.status === 'accepted').length;

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        eyebrow="Workforce · Training"
        title="Cohorts"
        description="Track defensora candidates, select them, invite the cohort to the training program, and hand the accepted group to scheduling."
        actions={
          <button
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-1.5 rounded-md bg-v2accent px-3 py-1.5 text-xs font-medium text-white hover:opacity-90"
          >
            <Plus className="w-4 h-4" /> New cohort
          </button>
        }
      />
      <PageContainer>
        {toast && (
          <div className="mb-4 flex items-center justify-between gap-3 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
            <span>{toast}</span>
            <button onClick={() => setToast(null)}>
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {loading ? (
          <div className="py-12 text-center text-ink-500">Loading cohorts…</div>
        ) : cohorts.length === 0 ? (
          <div className="py-12 text-center">
            <Users className="w-10 h-10 text-ink-400 mx-auto mb-2" />
            <p className="text-ink-500">No cohorts yet.</p>
            <button
              onClick={() => setShowCreate(true)}
              className="text-emerald-600 hover:text-emerald-700 font-medium mt-2"
            >
              Create your first cohort
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {cohorts.map((c) => (
              <button
                key={c.id}
                onClick={() => openCohort(c)}
                className="text-left bg-surface rounded-xl border border-v2border hover:shadow-md transition-shadow p-5"
              >
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-semibold text-ink">{c.name}</h3>
                  <span className="text-xs bg-paper-2 text-ink-500 px-2 py-0.5 rounded capitalize">{c.status}</span>
                </div>
                {c.lms_course_title && <p className="text-xs text-ink-500 mb-3">{c.lms_course_title}</p>}
                <div className="grid grid-cols-5 gap-1 text-center border-t border-v2border-muted pt-3">
                  {[
                    ['Total', c.total],
                    ['Cand.', c.candidates],
                    ['Sel.', c.selected],
                    ['Inv.', c.invited],
                    ['Acc.', c.accepted],
                  ].map(([label, n]) => (
                    <div key={label as string}>
                      <p className="text-lg font-semibold text-ink">{(n as number) ?? 0}</p>
                      <p className="text-[10px] text-ink-500">{label as string}</p>
                    </div>
                  ))}
                </div>
              </button>
            ))}
          </div>
        )}
      </PageContainer>

      {/* Create cohort */}
      {showCreate && (
        <Modal title="New cohort" onClose={() => setShowCreate(false)}>
          <input
            autoFocus
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Cohort name (e.g. Defensora Cohort 7)"
            className="w-full px-3 py-2 border border-v2border rounded-lg mb-4"
          />
          <div className="flex justify-end gap-2">
            <button onClick={() => setShowCreate(false)} className="px-4 py-2 border border-v2border rounded-lg">
              Cancel
            </button>
            <button onClick={createCohort} className="px-4 py-2 bg-emerald-600 text-white rounded-lg">
              Create
            </button>
          </div>
        </Modal>
      )}

      {/* Cohort detail (full screen) */}
      {selected && (
        <div className="fixed inset-0 z-50 bg-surface flex flex-col">
          <div className="h-full flex flex-col max-w-5xl w-full mx-auto">
            <div className="px-6 py-4 border-b border-v2border flex items-center justify-between">
              <div className="flex items-center gap-3 min-w-0">
                <button onClick={() => setSelected(null)} className="p-2 hover:bg-paper-2 rounded shrink-0">
                  <ChevronRight className="w-5 h-5 rotate-180" />
                </button>
                <h2 className="text-lg font-semibold text-ink truncate">{selected.name}</h2>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowAdd(true)}
                  className="px-3 py-2 bg-emerald-600 text-white rounded-lg text-sm flex items-center gap-1"
                >
                  <UserPlus className="w-4 h-4" /> Add members
                </button>
                <button
                  onClick={() => setShowInvite(true)}
                  className="px-3 py-2 border border-v2border rounded-lg text-sm flex items-center gap-1"
                >
                  <GraduationCap className="w-4 h-4" /> Invite to program
                </button>
                <button
                  onClick={() => setShowMessage(true)}
                  className="px-3 py-2 border border-v2border rounded-lg text-sm flex items-center gap-1"
                >
                  <Send className="w-4 h-4" /> Message
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="px-6 border-b border-v2border flex items-center gap-1">
              {(
                [
                  ['members', 'Members'],
                  ['sources', 'Sources'],
                  ['acceptance', 'Acceptance'],
                ] as [DetailTab, string][]
              ).map(([k, label]) => (
                <button
                  key={k}
                  onClick={() => setTab(k)}
                  className={`px-3 py-2.5 text-sm font-medium border-b-2 -mb-px ${
                    tab === k
                      ? 'border-emerald-500 text-emerald-700'
                      : 'border-transparent text-ink-500 hover:text-ink'
                  }`}
                >
                  {label}
                  {k === 'acceptance' && acceptedCount > 0 && (
                    <span className="ml-1.5 text-xs bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full">
                      {acceptedCount}
                    </span>
                  )}
                </button>
              ))}
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {tab === 'members' && (
                <div className="space-y-6">
                  {STATUS_ORDER.filter((s) => members.some((m) => m.status === s)).map((status) => {
                    const count = members.filter((m) => m.status === status).length;
                    const next = NEXT[status];
                    return (
                      <div key={status}>
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-500">
                            <span className={`px-1.5 py-0.5 rounded ${STATUS_COLORS[status] || ''}`}>{status}</span> (
                            {count})
                          </h3>
                          {next && count > 0 && (
                            <button
                              onClick={() => bulkSetStatus(status, next)}
                              className="text-xs font-medium text-emerald-700 hover:text-emerald-800 bg-emerald-50 px-2.5 py-1 rounded-full"
                            >
                              Move all {count} → {next}
                            </button>
                          )}
                        </div>
                        <div className="border border-v2border rounded-lg divide-y divide-v2border-muted">
                          {members
                            .filter((m) => m.status === status)
                            .map((m) => (
                              <div key={m.id} className="flex items-center justify-between px-3 py-2">
                                <div className="min-w-0">
                                  <p className="text-sm font-medium text-ink truncate">{m.name}</p>
                                  <p className="text-xs text-ink-500 truncate">
                                    {m.email || m.phone || `Member ${m.member_id}`} · {m.source}
                                  </p>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                  {next && (
                                    <button
                                      onClick={() => setMemberStatus(m.member_id, next)}
                                      className="text-xs font-medium text-white bg-emerald-600 hover:bg-emerald-700 px-2.5 py-1 rounded-full"
                                    >
                                      Move to {next}
                                    </button>
                                  )}
                                  {status !== 'declined' && status !== 'withdrawn' && (
                                    <button
                                      onClick={() => setMemberStatus(m.member_id, 'declined')}
                                      className="text-xs text-ink-400 hover:text-red-600"
                                      title="Decline"
                                    >
                                      <X className="w-3.5 h-3.5" />
                                    </button>
                                  )}
                                </div>
                              </div>
                            ))}
                        </div>
                      </div>
                    );
                  })}
                  {members.length === 0 && (
                    <p className="text-center text-ink-500 py-8">
                      No members yet. Add candidates by hand or link a source.
                    </p>
                  )}
                </div>
              )}

              {tab === 'sources' && <SourcesTab cohortId={selected.id} onChanged={refreshDetail} setToast={setToast} />}

              {tab === 'acceptance' && (
                <AcceptanceTab cohortId={selected.id} onChanged={refreshDetail} setToast={setToast} />
              )}
            </div>
          </div>

          {showAdd && (
            <AddMembersModal
              cohortId={selected.id}
              onClose={() => setShowAdd(false)}
              onDone={async () => {
                setShowAdd(false);
                await refreshDetail();
              }}
            />
          )}
          {showInvite && (
            <InviteToProgramPage
              cohortId={selected.id}
              cohortName={selected.name}
              selectedCount={members.filter((m) => m.status === 'selected').length}
              onClose={() => setShowInvite(false)}
              onDone={(msg) => {
                setShowInvite(false);
                setToast(msg);
                setTab('acceptance');
                refreshDetail();
              }}
            />
          )}
          {showMessage && (
            <MessageCohortModal
              cohortId={selected.id}
              cohortName={selected.name}
              recipientCount={members.filter((m) => m.status === 'selected' || m.status === 'enrolled').length}
              onClose={() => setShowMessage(false)}
              onDone={(msg) => {
                setShowMessage(false);
                setToast(msg);
              }}
            />
          )}
        </div>
      )}
    </div>
  );
}

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-surface rounded-xl shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-ink">{title}</h2>
          <button onClick={onClose} className="p-2 hover:bg-paper-2 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

interface EligibleLearner {
  member_id: number;
  full_name: string;
  email: string | null;
}

function AddMembersModal({ cohortId, onClose, onDone }: { cohortId: number; onClose: () => void; onDone: () => void }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState<EligibleLearner[]>([]);
  const [picked, setPicked] = useState<Record<number, boolean>>({});
  const [status, setStatus] = useState('candidate');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(async () => {
      const res = await authFetch(
        api(`/api/workforce/training-series/eligible-learners?limit=100${q ? `&q=${encodeURIComponent(q)}` : ''}`),
      );
      if (res.ok) setResults((await res.json()).learners || []);
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const pickedIds = Object.keys(picked)
    .map(Number)
    .filter((id) => picked[id]);
  const allShownPicked = results.length > 0 && results.every((r) => picked[r.member_id]);
  const toggleAllShown = () =>
    setPicked((p) => {
      const next = { ...p };
      const target = !allShownPicked;
      results.forEach((r) => {
        next[r.member_id] = target;
      });
      return next;
    });

  const save = async () => {
    if (pickedIds.length === 0) return;
    setSaving(true);
    setError(null);
    const res = await authFetch(api(`/api/workforce/cohorts/${cohortId}/members`), {
      method: 'POST',
      jsonBody: { member_ids: pickedIds, status, source: 'nomination' },
    });
    setSaving(false);
    if (res.ok) {
      onDone();
    } else {
      const d = await res.json().catch(() => ({}) as any);
      setError(typeof d.detail === 'string' ? d.detail : 'Could not add members');
    }
  };

  return (
    <Modal title="Add members" onClose={onClose}>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xs text-ink-500">Add as</span>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="text-sm border border-v2border rounded px-2 py-1"
        >
          <option value="candidate">Candidate</option>
          <option value="selected">Selected</option>
        </select>
      </div>
      <div className="relative mb-2">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search members…"
          className="w-full pl-10 pr-3 py-2 border border-v2border rounded-lg"
        />
      </div>
      {results.length > 0 && (
        <label className="flex items-center gap-2 px-1 mb-1 text-xs text-ink-500 cursor-pointer">
          <input type="checkbox" checked={allShownPicked} onChange={toggleAllShown} />
          Select all shown ({results.length})
        </label>
      )}
      <div className="max-h-64 overflow-y-auto border border-v2border-muted rounded-lg divide-y divide-v2border-muted mb-3">
        {results.map((r) => (
          <label key={r.member_id} className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-paper">
            <input
              type="checkbox"
              checked={!!picked[r.member_id]}
              onChange={(e) => setPicked((p) => ({ ...p, [r.member_id]: e.target.checked }))}
            />
            <span className="text-sm text-ink">{r.full_name}</span>
            <span className="text-xs text-ink-500 ml-auto">{r.email}</span>
          </label>
        ))}
        {results.length === 0 && <p className="text-sm text-ink-500 p-3">No members found.</p>}
      </div>
      {error && <p className="text-xs text-red-600 mb-2">{error}</p>}
      <div className="flex items-center justify-between">
        <span className="text-xs text-ink-500">{pickedIds.length} selected</span>
        <div className="flex gap-2">
          <button onClick={onClose} className="px-4 py-2 border border-v2border rounded-lg">
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving || pickedIds.length === 0}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg disabled:opacity-50"
          >
            {saving ? 'Adding…' : `Add ${pickedIds.length || ''}`}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Sources tab (Gap 2) — link a dynamic candidate source, preview its FULL member
// list, and keep it auto-syncing so new group members / respondents flow in.
// ---------------------------------------------------------------------------

interface SourceMember {
  member_id: number;
  name: string;
  email: string | null;
  in_cohort: boolean;
}

const SOURCE_KINDS: { k: 'group' | 'survey' | 'campaign' | 'intake'; l: string }[] = [
  { k: 'group', l: 'Communications group' },
  { k: 'intake', l: 'Public intake form' },
  { k: 'survey', l: 'Survey responders' },
  { k: 'campaign', l: 'Outreach responders' },
];

function SourcesTab({
  cohortId,
  onChanged,
  setToast,
}: {
  cohortId: number;
  onChanged: () => void;
  setToast: (s: string) => void;
}) {
  const [linked, setLinked] = useState<LinkedSource[]>([]);
  const [sourceType, setSourceType] = useState<'group' | 'survey' | 'campaign' | 'intake'>('group');
  const [ref, setRef] = useState('');
  const [options, setOptions] = useState<{ id: string; name: string }[]>([]);
  const [preview, setPreview] = useState<{ members: SourceMember[]; total: number; new: number; label: string } | null>(
    null,
  );
  const [previewing, setPreviewing] = useState(false);
  const [defaultStatus, setDefaultStatus] = useState('candidate');
  const [autoSync, setAutoSync] = useState(true);
  const [busy, setBusy] = useState(false);

  const loadLinked = useCallback(async () => {
    const res = await authFetch(api(`/api/workforce/cohorts/${cohortId}/sources`));
    if (res.ok) setLinked((await res.json()).sources || []);
  }, [cohortId]);

  useEffect(() => {
    loadLinked();
  }, [loadLinked]);

  // Load the pick-list for the chosen source type.
  useEffect(() => {
    setRef('');
    setOptions([]);
    setPreview(null);
    if (sourceType === 'intake') return;
    (async () => {
      const url = sourceType === 'group' ? '/api/groups' : sourceType === 'survey' ? '/api/surveys' : '/api/campaigns';
      const res = await authFetch(api(url));
      if (!res.ok) return;
      const data = await res.json();
      const items = Array.isArray(data) ? data : data.groups || data.surveys || data.campaigns || data.items || [];
      setOptions(items.map((x: any) => ({ id: String(x.id), name: x.name || x.title || `#${x.id}` })));
    })();
  }, [sourceType]);

  const runPreview = useCallback(async () => {
    if (sourceType !== 'intake' && !ref) {
      setPreview(null);
      return;
    }
    setPreviewing(true);
    try {
      const res = await authFetch(api(`/api/workforce/cohorts/${cohortId}/sources/preview`), {
        method: 'POST',
        jsonBody: { source_type: sourceType, source_ref: sourceType === 'intake' ? undefined : ref },
      });
      if (res.ok) setPreview(await res.json());
      else setPreview(null);
    } finally {
      setPreviewing(false);
    }
  }, [cohortId, sourceType, ref]);

  useEffect(() => {
    runPreview();
  }, [runPreview]);

  const linkSource = async () => {
    if (sourceType !== 'intake' && !ref) return;
    setBusy(true);
    const res = await authFetch(api(`/api/workforce/cohorts/${cohortId}/sources`), {
      method: 'POST',
      jsonBody: {
        source_type: sourceType,
        source_ref: sourceType === 'intake' ? undefined : ref,
        default_status: defaultStatus,
        auto_sync: autoSync,
      },
    });
    setBusy(false);
    if (res.ok) {
      const d = await res.json();
      setToast(
        `Linked ${d.label} — added ${d.added} member(s)${autoSync ? '; new members will sync automatically.' : '.'}`,
      );
      setPreview(null);
      setRef('');
      await loadLinked();
      onChanged();
    }
  };

  const syncNow = async (id: number) => {
    const res = await authFetch(api(`/api/workforce/cohorts/${cohortId}/sources/${id}/sync`), { method: 'POST' });
    if (res.ok) {
      const d = await res.json();
      setToast(`Synced — added ${d.added} new member(s).`);
      await loadLinked();
      onChanged();
    }
  };

  const toggleAuto = async (s: LinkedSource) => {
    await authFetch(api(`/api/workforce/cohorts/${cohortId}/sources/${s.id}`), {
      method: 'PATCH',
      jsonBody: { auto_sync: !s.auto_sync },
    });
    await loadLinked();
  };

  const unlink = async (id: number) => {
    await authFetch(api(`/api/workforce/cohorts/${cohortId}/sources/${id}`), { method: 'DELETE' });
    await loadLinked();
  };

  return (
    <div className="space-y-8">
      {/* Linked sources */}
      <div>
        <h3 className="text-sm font-semibold text-ink mb-2">Linked sources</h3>
        {linked.length === 0 ? (
          <p className="text-sm text-ink-500">
            No linked sources yet. Link one below to pull members in — and keep pulling new ones automatically.
          </p>
        ) : (
          <div className="border border-v2border rounded-lg divide-y divide-v2border-muted">
            {linked.map((s) => (
              <div key={s.id} className="flex items-center justify-between px-3 py-2.5 gap-3">
                <div className="min-w-0 flex items-center gap-2">
                  <Link2 className="w-4 h-4 text-ink-400 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-ink truncate">{s.source_label || s.source_type}</p>
                    <p className="text-xs text-ink-500">
                      Adds as {s.default_status}
                      {s.last_synced_at ? ` · last synced ${new Date(s.last_synced_at).toLocaleString()}` : ''}
                      {typeof s.last_added === 'number' ? ` · +${s.last_added} last run` : ''}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <label className="flex items-center gap-1 text-xs text-ink-500 cursor-pointer">
                    <input type="checkbox" checked={s.auto_sync} onChange={() => toggleAuto(s)} />
                    Auto-sync
                  </label>
                  <button
                    onClick={() => syncNow(s.id)}
                    className="text-xs px-2 py-1 border border-v2border rounded flex items-center gap-1 hover:bg-paper-2"
                  >
                    <RefreshCw className="w-3.5 h-3.5" /> Sync now
                  </button>
                  <button onClick={() => unlink(s.id)} className="text-ink-400 hover:text-red-600" title="Unlink">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add / link a source */}
      <div>
        <h3 className="text-sm font-semibold text-ink mb-2">Add from a source</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
          {SOURCE_KINDS.map((s) => (
            <button
              key={s.k}
              onClick={() => setSourceType(s.k)}
              className={`px-3 py-2 rounded-lg border text-sm text-left ${
                sourceType === s.k
                  ? 'border-emerald-400 bg-emerald-50 text-emerald-700'
                  : 'border-v2border text-ink-600'
              }`}
            >
              {s.l}
            </button>
          ))}
        </div>

        {sourceType !== 'intake' && (
          <div className="mb-3">
            <label className="text-xs text-ink-500 block mb-1">
              {sourceType === 'group' ? 'Group' : sourceType === 'survey' ? 'Survey' : 'Campaign'}
            </label>
            <select
              value={ref}
              onChange={(e) => setRef(e.target.value)}
              className="w-full px-3 py-2 border border-v2border rounded-lg text-sm bg-surface"
            >
              <option value="">— Select —</option>
              {options.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Full member preview */}
        <div className="border border-v2border rounded-lg mb-3">
          <div className="flex items-center justify-between px-3 py-2 border-b border-v2border-muted bg-paper-2">
            <p className="text-xs font-medium text-ink-600">
              {previewing
                ? 'Resolving…'
                : preview
                  ? `${preview.total} member(s) — ${preview.new} new to this cohort`
                  : sourceType === 'intake'
                    ? 'Everyone who came in through this org’s public intake form'
                    : 'Select a source to preview its members'}
            </p>
            {preview && preview.total > 0 && (
              <button onClick={runPreview} className="text-xs text-emerald-700 flex items-center gap-1">
                <RefreshCw className="w-3 h-3" /> Refresh
              </button>
            )}
          </div>
          <div className="max-h-72 overflow-y-auto divide-y divide-v2border-muted">
            {preview?.members.map((m) => (
              <div key={m.member_id} className="flex items-center justify-between px-3 py-1.5">
                <span className="text-sm text-ink truncate">{m.name}</span>
                <span className="text-xs text-ink-500 ml-auto mr-2 truncate">{m.email}</span>
                {m.in_cohort && <span className="text-[10px] text-ink-400 shrink-0">already in cohort</span>}
              </div>
            ))}
            {preview && preview.members.length === 0 && (
              <p className="text-sm text-ink-500 p-3">This source has no matching members yet.</p>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-xs text-ink-500">Add as</span>
            <select
              value={defaultStatus}
              onChange={(e) => setDefaultStatus(e.target.value)}
              className="text-sm border border-v2border rounded px-2 py-1"
            >
              <option value="candidate">Candidate</option>
              <option value="selected">Selected</option>
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm text-ink-600 cursor-pointer">
            <input type="checkbox" checked={autoSync} onChange={(e) => setAutoSync(e.target.checked)} />
            Keep in sync (auto-add new members / respondents)
          </label>
          <button
            onClick={linkSource}
            disabled={busy || (sourceType !== 'intake' && !ref)}
            className="ml-auto px-4 py-2 bg-emerald-600 text-white rounded-lg disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Layers className="w-4 h-4" />}
            {autoSync ? 'Link & add' : 'Add these members'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Acceptance tab (Gap 3) — track who accepted the program invite; the accepted
// set is what scheduling sends session invites to.
// ---------------------------------------------------------------------------

interface AcceptanceMember {
  member_id: number;
  name: string;
  email: string | null;
  cohort_status: string;
  acceptance: 'invited' | 'accepted' | 'declined';
}

function AcceptanceTab({
  cohortId,
  onChanged,
  setToast,
}: {
  cohortId: number;
  onChanged: () => void;
  setToast: (s: string) => void;
}) {
  const [data, setData] = useState<{
    invited: number;
    accepted: number;
    declined: number;
    series_id: string | null;
    members: AcceptanceMember[];
  } | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(api(`/api/workforce/cohorts/${cohortId}/acceptance`));
      if (res.ok) setData(await res.json());
    } finally {
      setLoading(false);
    }
  }, [cohortId]);

  useEffect(() => {
    load();
  }, [load]);

  const record = async (memberId: number, accepted: boolean) => {
    const res = await authFetch(api(`/api/workforce/cohorts/${cohortId}/record-acceptance`), {
      method: 'POST',
      jsonBody: { member_ids: [memberId], accepted },
    });
    if (res.ok) {
      await load();
      onChanged();
    }
  };

  const recordAllInvited = async () => {
    if (!data) return;
    const ids = data.members.filter((m) => m.acceptance === 'invited').map((m) => m.member_id);
    if (ids.length === 0) return;
    const res = await authFetch(api(`/api/workforce/cohorts/${cohortId}/record-acceptance`), {
      method: 'POST',
      jsonBody: { member_ids: ids, accepted: true },
    });
    if (res.ok) {
      setToast(`Recorded acceptance for ${ids.length} member(s).`);
      await load();
      onChanged();
    }
  };

  if (loading) return <p className="text-sm text-ink-500 py-8 text-center">Loading acceptance…</p>;
  if (!data || data.members.length === 0)
    return (
      <div className="text-center text-ink-500 py-10">
        <GraduationCap className="w-9 h-9 mx-auto mb-2 text-ink-400" />
        <p>No one invited to the program yet.</p>
        <p className="text-xs mt-1">Use “Invite to program” to invite the selected members and gather acceptance.</p>
      </div>
    );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        {[
          ['Invited', data.invited, 'text-indigo-700'],
          ['Accepted', data.accepted, 'text-emerald-700'],
          ['Declined', data.declined, 'text-red-700'],
        ].map(([label, n, color]) => (
          <div key={label as string} className="border border-v2border rounded-lg p-3 text-center">
            <p className={`text-2xl font-semibold ${color as string}`}>{n as number}</p>
            <p className="text-xs text-ink-500">{label as string}</p>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between">
        <p className="text-xs text-ink-500">
          Accepted members are ready to schedule — invite them to sessions from Training → Schedule.
        </p>
        {data.members.some((m) => m.acceptance === 'invited') && (
          <button
            onClick={recordAllInvited}
            className="text-xs font-medium text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full"
          >
            Mark all invited as accepted
          </button>
        )}
      </div>

      <div className="border border-v2border rounded-lg divide-y divide-v2border-muted">
        {data.members.map((m) => (
          <div key={m.member_id} className="flex items-center justify-between px-3 py-2 gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-ink truncate">{m.name}</p>
              <p className="text-xs text-ink-500 truncate">{m.email || `Member ${m.member_id}`}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {m.acceptance === 'accepted' ? (
                <span className="text-xs font-medium text-emerald-700 flex items-center gap-1">
                  <CheckCircle2 className="w-4 h-4" /> Accepted
                </span>
              ) : m.acceptance === 'declined' ? (
                <span className="text-xs font-medium text-red-600">Declined</span>
              ) : (
                <>
                  <button
                    onClick={() => record(m.member_id, true)}
                    className="text-xs font-medium text-white bg-emerald-600 hover:bg-emerald-700 px-2.5 py-1 rounded-full"
                  >
                    Mark accepted
                  </button>
                  <button
                    onClick={() => record(m.member_id, false)}
                    className="text-xs text-ink-400 hover:text-red-600"
                    title="Mark declined"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Invite to program (Gap 3) — full-page, templated. Invites the selected members
// and moves them to "invited"; acceptance is tracked on the Acceptance tab.
// ---------------------------------------------------------------------------

function InviteToProgramPage({
  cohortId,
  cohortName,
  selectedCount,
  onClose,
  onDone,
}: {
  cohortId: number;
  cohortName: string;
  selectedCount: number;
  onClose: () => void;
  onDone: (msg: string) => void;
}) {
  const [series, setSeries] = useState<SeriesSummary[]>([]);
  const [seriesId, setSeriesId] = useState('');
  const [channels, setChannels] = useState<string[]>(['email', 'portal']);
  const [templates, setTemplates] = useState<{ id: number; name: string; subject: string | null; body: string }[]>([]);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      const res = await authFetch(api('/api/workforce/training-series'));
      if (res.ok) setSeries((await res.json()).series || []);
      const tres = await authFetch(api('/api/communication-templates'));
      if (tres.ok) {
        const data = await tres.json();
        const items = Array.isArray(data) ? data : data.templates || data.items || [];
        setTemplates(
          items
            .filter((t: any) => (t.template_type || t.communication_method) === 'email')
            .map((t: any) => ({ id: t.id, name: t.name, subject: t.subject, body: t.body })),
        );
      }
    })();
  }, []);

  const toggle = (c: string) => setChannels((cs) => (cs.includes(c) ? cs.filter((x) => x !== c) : [...cs, c]));

  const invite = async () => {
    if (!seriesId) return;
    setBusy(true);
    const res = await authFetch(api(`/api/workforce/cohorts/${cohortId}/invite-to-series`), {
      method: 'POST',
      jsonBody: {
        series_id: seriesId,
        channels,
        member_statuses: ['selected'],
        set_status: 'invited',
        subject: subject || undefined,
        body: body || undefined,
      },
    });
    setBusy(false);
    if (res.ok) {
      const d = await res.json();
      onDone(`Invited ${d.invited_members} selected member(s) to the program; notified ${d.notified}.`);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-surface flex flex-col">
      <div className="h-full flex flex-col max-w-3xl w-full mx-auto">
        <div className="px-6 py-4 border-b border-v2border flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="p-2 hover:bg-paper-2 rounded" title="Back">
              <ChevronRight className="w-5 h-5 rotate-180" />
            </button>
            <div>
              <h2 className="text-lg font-semibold text-ink">Invite {cohortName} to a program</h2>
              <p className="text-sm text-ink-500">
                Invites the {selectedCount} selected member{selectedCount === 1 ? '' : 's'} and tracks who accepts.
              </p>
            </div>
          </div>
          <button
            onClick={invite}
            disabled={busy || !seriesId}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <GraduationCap className="w-4 h-4" />} Send invites
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          <div>
            <label className="text-xs font-medium text-ink-500 block mb-1">Training program (series)</label>
            <select
              value={seriesId}
              onChange={(e) => setSeriesId(e.target.value)}
              className="w-full px-3 py-2 border border-v2border rounded-lg"
            >
              <option value="">— Select a series —</option>
              {series.map((s) => (
                <option key={s.series_id} value={String(s.series_id)}>
                  {s.series_title} ({s.sessions} sessions)
                </option>
              ))}
            </select>
          </div>

          <div>
            <p className="text-xs font-medium text-ink-500 mb-1.5">Notify via</p>
            <div className="flex items-center gap-2">
              {[
                { k: 'email', l: 'Email' },
                { k: 'sms', l: 'Text' },
                { k: 'portal', l: 'Member portal' },
              ].map((c) => (
                <button
                  key={c.k}
                  onClick={() => toggle(c.k)}
                  className={`px-2.5 py-1 rounded-full border text-xs ${
                    channels.includes(c.k)
                      ? 'border-emerald-400 bg-emerald-50 text-emerald-700'
                      : 'border-v2border text-ink-500'
                  }`}
                >
                  {c.l}
                </button>
              ))}
            </div>
          </div>

          {templates.length > 0 && (
            <div>
              <label className="text-xs font-medium text-ink-500 block mb-1">Start from a template</label>
              <select
                onChange={(e) => {
                  const t = templates.find((x) => String(x.id) === e.target.value);
                  if (t) {
                    setSubject(t.subject || '');
                    setBody(t.body || '');
                  }
                }}
                className="w-full px-3 py-2 border border-v2border rounded-lg text-sm bg-surface"
                defaultValue=""
              >
                <option value="">— None (use default invite copy) —</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {channels.includes('email') && (
            <div>
              <label className="text-xs font-medium text-ink-500 block mb-1">Email subject (optional)</label>
              <input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Leave blank to use the default invite subject"
                className="w-full px-3 py-2 border border-v2border rounded-lg text-sm"
              />
            </div>
          )}
          <div>
            <label className="text-xs font-medium text-ink-500 block mb-1">Invite message (optional)</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={8}
              placeholder="Leave blank to use the default invite copy.  Use {{first_name}} to personalize."
              className="w-full px-3 py-2 border border-v2border rounded-lg text-sm"
            />
          </div>
          <p className="text-xs text-ink-400">
            Each invite email carries a calendar invitation for every session (one .ics per session, so all sessions
            land on the learner’s calendar). Invited members appear on the Acceptance tab.
          </p>
        </div>
      </div>
    </div>
  );
}

function MessageCohortModal({
  cohortId,
  cohortName,
  recipientCount,
  onClose,
  onDone,
}: {
  cohortId: number;
  cohortName: string;
  recipientCount: number;
  onClose: () => void;
  onDone: (msg: string) => void;
}) {
  const [channels, setChannels] = useState<string[]>(['email']);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [templates, setTemplates] = useState<{ id: number; name: string; subject: string | null; body: string }[]>([]);
  const [busy, setBusy] = useState(false);
  const toggle = (c: string) => setChannels((cs) => (cs.includes(c) ? cs.filter((x) => x !== c) : [...cs, c]));

  // Reuse the org's communication templates as message starters.
  useEffect(() => {
    (async () => {
      const res = await authFetch(api('/api/communication-templates'));
      if (res.ok) {
        const data = await res.json();
        const items = Array.isArray(data) ? data : data.templates || data.items || [];
        setTemplates(
          items
            .filter((t: any) => (t.template_type || t.communication_method) === 'email')
            .map((t: any) => ({ id: t.id, name: t.name, subject: t.subject, body: t.body })),
        );
      }
    })();
  }, []);

  const send = async () => {
    if (!body.trim()) return;
    setBusy(true);
    const res = await authFetch(api(`/api/workforce/cohorts/${cohortId}/message`), {
      method: 'POST',
      jsonBody: { channels, subject: subject || undefined, body },
    });
    setBusy(false);
    if (res.ok) {
      const d = await res.json();
      onDone(`Message sent to ${d.recipients} member(s) (email ${d.sent?.email || 0}, sms ${d.sent?.sms || 0}).`);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-surface flex flex-col">
      <div className="h-full flex flex-col max-w-3xl w-full mx-auto">
        <div className="px-6 py-4 border-b border-v2border flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="p-2 hover:bg-paper-2 rounded" title="Back">
              <ChevronRight className="w-5 h-5 rotate-180" />
            </button>
            <div>
              <h2 className="text-lg font-semibold text-ink">Message {cohortName}</h2>
              <p className="text-sm text-ink-500">
                To {recipientCount} selected &amp; enrolled member{recipientCount === 1 ? '' : 's'}
              </p>
            </div>
          </div>
          <button
            onClick={send}
            disabled={busy || !body.trim()}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <div>
            <p className="text-xs font-medium text-ink-500 mb-1.5">Channel</p>
            <div className="flex items-center gap-2">
              {[
                { k: 'email', l: 'Email' },
                { k: 'sms', l: 'Text (SMS)' },
              ].map((c) => (
                <button
                  key={c.k}
                  onClick={() => toggle(c.k)}
                  className={`px-3 py-1.5 rounded-lg border text-sm ${
                    channels.includes(c.k)
                      ? 'border-emerald-400 bg-emerald-50 text-emerald-700'
                      : 'border-v2border text-ink-500'
                  }`}
                >
                  {c.l}
                </button>
              ))}
            </div>
          </div>

          {templates.length > 0 && (
            <div>
              <label className="text-xs font-medium text-ink-500 block mb-1">Start from a template (Communications)</label>
              <select
                onChange={(e) => {
                  const t = templates.find((x) => String(x.id) === e.target.value);
                  if (t) {
                    setSubject(t.subject || '');
                    setBody(t.body || '');
                  }
                }}
                className="w-full px-3 py-2 border border-v2border rounded-lg text-sm bg-surface"
                defaultValue=""
              >
                <option value="">— None —</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {channels.includes('email') && (
            <div>
              <label className="text-xs font-medium text-ink-500 block mb-1">Subject</label>
              <input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Email subject"
                className="w-full px-3 py-2 border border-v2border rounded-lg text-sm"
              />
            </div>
          )}
          <div>
            <label className="text-xs font-medium text-ink-500 block mb-1">Message</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={12}
              placeholder="Write your message…  (use {{first_name}} to personalize)"
              className="w-full px-3 py-2 border border-v2border rounded-lg text-sm"
            />
          </div>
          <p className="text-xs text-ink-400">
            Sends through the platform's communication paths (SendGrid email / Twilio SMS) and is logged to each member's
            communication history. Opted-out members are skipped.
          </p>
        </div>
      </div>
    </div>
  );
}
